import React, { useEffect, useState } from "react";
import { CurrentPlanProtag } from "../../../StaticPages/Pages/Skeleton/CurrentplanSkeleton";
import { useBilling } from "../../../../context/BillingContext";
import { useRazorpay } from "../../../../context/RazorpayContext";

const PaymentDetails = () => {
  const {
    currentPlan,
    loading,
    paymentMethods,
    refetchBilling,
    data,
    refetchPaymentMethods,
  } = useBilling();
  const { handleCreatePlan, handlePreviewPlan } = useRazorpay();
  const [isLoading, setIsLoading] = useState(false);

  // envent listner whenever plan is upgrade

  useEffect(() => {
    const handleBillingUpdate = () => {
      refetchPaymentMethods(); // payment method refresh
      refetchBilling(); // current plan refresh
    };

    window.addEventListener("billingUpdated", handleBillingUpdate);

    return () => {
      window.removeEventListener("billingUpdated", handleBillingUpdate);
    };
  }, []);

  // Mask card number helper
  // const maskCardNumber = (last4 = "4232") => `•••• •••• •••• ${last4}` || paymentMethods?.id
  const maskCardNumber = (value) => {
    // If numeric → mask it
    if (/^\d+$/.test(value)) {
      return `•••• •••• •••• ${value}`;
    }

    // If not numeric (string like "razorpay") → return as is
    return value;
  };

  // Format expiry
  const formatExpiry = (month, year) => month && year && `${month}/${year}`;

  const handleRegeneratePaymentUrl = async (e) => {
    handlePreviewPlan(e, currentPlan?.id, "paymentMethod", false);
    // await handleCreatePlan(e, setIsLoading, 'generatePaymentUrl', null, refetchBilling)
  };

  const isFreeTrial =
    currentPlan?.is_free && currentPlan?.subscription?.amount === "₹0.00";

  const getDaysLeft = (endDate) => {
    const end = new Date(endDate);
    const today = new Date();
    return Math.max(0, Math.ceil((end - today) / (1000 * 60 * 60 * 24)));
  };

  // calculate start date from billing_cycle
  let trialEndDate = currentPlan?.is_free
    ? currentPlan?.subscription?.billing_cycle?.split(" - ")[1]
    : currentPlan?.subscription?.next_billing_date;

  let endDate = new Date(trialEndDate);

  const today = new Date();
  const startDateStr =
    currentPlan?.subscription?.billing_cycle?.split(" - ")[0];
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split(" ");
    return new Date(`${month} ${day}, 20${year}`);
  };
  const startDate = parseDate(startDateStr);
  const daysPassed = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
  const totalDays = currentPlan?.trial?.days || 14;
  const daysLeft = Math.max(0, totalDays - daysPassed);

  const safeDaysLeft = Math.min(daysLeft, totalDays);

  // remaining-based progress
  const progress = (daysLeft / totalDays) * 100;

  const billingMode = currentPlan?.subscription?.billing_mode?.toLowerCase();

  const paymentMethod = paymentMethods?.method?.toUpperCase();

  const showPaymentRow =
    billingMode === "autopay" && ["CARD", "UPI"].includes(paymentMethod);

  return (
    <div className="payment-main-card h-100 w-100">
      {/* HEADER */}
      <div className="payment-top w-100">
        <div className="payment-title w-100">
          <h5>Payment Overview</h5>

          {!loading?.currentPlan &&
            currentPlan?.subscription?.status === "active" && (
              <span className="status-badge">Active</span>
            )}
        </div>
      </div>
      {/* CONTENT */}
      <div className="payment-content h-100">
        {/* LEFT BLOCK */}
        {isFreeTrial ? (
          <>
            <div className="payment-left">
              <div className="payment-row">
                <span>Ends On</span>
                <strong>{trialEndDate}</strong>
              </div>

              <div className="payment-row">
                <span>Status</span>
                <strong>No payment required</strong>
              </div>

              {/*  Only here show progress */}
              <div className="payment-row no-border">
                <span className="text-nowrap">{daysLeft} days left</span>

                <div className="trial-progress">
                  <div
                    className="trial-progress-bar"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="payment-left">
              <div className="payment-row">
                <div className="d-flex gap-2">
                  <i className="bi bi-credit-card"></i>
                  <span>Billing Mode</span>
                </div>
                <div className="d-flex gap-2">
                  <strong>
                    {loading?.currentPlan ? (
                      <span className="payment-loading-text"></span>
                    ) : currentPlan?.subscription?.billing_mode ? (
                      `${currentPlan.subscription.billing_mode.charAt(0).toUpperCase()}${currentPlan.subscription.billing_mode.slice(1)}`
                    ) : (
                      "Manual"
                    )}
                  </strong>
                  {loading?.currentPlan ? (
                    <span className="payment-loading-text"></span>
                  ) : (
                    <a
                      className="manage-link"
                      onClick={handleRegeneratePaymentUrl}
                    >
                      Manage
                    </a>
                  )}
                </div>
              </div>

              {/* Conditional Row */}
              {showPaymentRow ? (
                paymentMethod === "CARD" ? (
                  <div className="payment-row">
                    <div className="d-flex gap-2">
                      <i className="bi bi-calendar"></i>
                      <span>Card No.</span>
                    </div>

                    <strong>
                      {paymentMethods?.id || "XXXX XXXX XXXX 1234"}
                    </strong>
                  </div>
                ) : paymentMethod === "UPI" ? (
                  <div className="payment-row">
                    <div className="d-flex gap-2">
                      <i className="bi bi-phone"></i>
                      <span>UPI ID</span>
                    </div>

                    <strong>{paymentMethods?.upi_id || "-----@upi"}</strong>
                  </div>
                ) : paymentMethod === "NETBANKING" ? (
                  <div className="payment-row">
                    <div className="d-flex gap-2">
                      <i className="bi bi-bank"></i>
                      <span>Bank</span>
                    </div>

                    <strong>{paymentMethods?.bank || "Net Banking"}</strong>
                  </div>
                ) : paymentMethod === "WALLET" ? (
                  <div className="payment-row">
                    <div className="d-flex gap-2">
                      <i className="bi bi-wallet2"></i>
                      <span>Wallet</span>
                    </div>

                    <strong>{paymentMethods?.wallet || "Wallet"}</strong>
                  </div>
                ) : null
              ) : null}

              <div className="payment-row">
                <div className="d-flex gap-2">
                  <i className="bi bi-calendar-event"></i>
                  <span>Next Billing Date</span>
                </div>
                {loading?.currentPlan ? (
                  <span className="payment-loading-text"></span>
                ) : (
                  <strong>
                    {currentPlan?.subscription?.next_billing_date || "N/A"}
                  </strong>
                )}
              </div>

              <div className="payment-row no-border">
                <div className="d-flex gap-2">
                  <i className="bi bi-currency-rupee"></i>
                  <span>Next Billing Amount</span>
                </div>
                {loading?.currentPlan ? (
                  <span className="payment-loading-text"></span>
                ) : (
                  <strong>{currentPlan?.subscription?.amount || "₹---"}</strong>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentDetails;
